// Le tableau des retours : en-tête, filtres, liste ou formulaire. Monté dans
// le tiroir du widget, en pleine page dans le tableau de suivi, ou seul
// n'importe où (mount(el, 'board')). Porté de FeedbackModal.
import type { JSX } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import { DEFAULT_FILTERS, useFeedbaacks } from '../context'
import { ArrowLeft, Plus, Sliders, X } from '../icons'
import { IconButton } from '../primitives'
import { useSelector } from '../store'
import { Composer } from './Composer'
import { Filters } from './Filters'
import { List } from './List'
import { AgentStatusDot } from './parts'

export function Board({ onClose, title }: { onClose?: () => void; title?: string }): JSX.Element {
  const { store, t } = useFeedbaacks()
  const composerOpen = useSelector(store, (s) => s.composerOpen)
  const filtersOpen = useSelector(store, (s) => s.filtersOpen)
  const activeFilters = useSelector(store, (s) =>
    (Object.keys(DEFAULT_FILTERS) as Array<keyof typeof DEFAULT_FILTERS>).filter((k) => (k === 'search' ? !!s.filters.search.trim() : s.filters[k] !== DEFAULT_FILTERS[k])).length,
  )
  return (
    <div class="fb-board" data-testid="feedbaacks-board">
      <header class="fb-board-header">
        {composerOpen && (
          <IconButton label={t('modal_back')} onClick={() => store.set({ composerOpen: false, editingId: null })}>
            <ArrowLeft size={16} />
          </IconButton>
        )}
        <h2 class="fb-board-title">{title ?? t('modal_title')}</h2>
        {!composerOpen && (
          <span class="fb-relative">
            <IconButton label={t('filters_toggle')} active={filtersOpen} testId="feedbaacks-filters-toggle" onClick={() => store.set({ filtersOpen: !filtersOpen })}>
              <Sliders size={14} />
            </IconButton>
            {activeFilters > 0 && <span class="fb-badge" aria-label={t('modal_filters_active_aria')}>{activeFilters}</span>}
          </span>
        )}
        <AgentStatusDot />
        <button type="button" class="fb-new-btn" aria-label={t('modal_new_aria')} title={`${t('modal_new')} (N)`} data-testid="feedbaacks-new"
          onClick={() => store.set({ composerOpen: true, editingId: null, selectedId: null })}>
          <Plus size={14} />
        </button>
        {onClose && (
          <IconButton label={t('modal_close')} onClick={onClose} testId="feedbaacks-close">
            <X size={14} />
          </IconButton>
        )}
      </header>
      {composerOpen ? (
        <Composer />
      ) : (
        <>
          <Filters />
          <div class="fb-scroll">
            <List />
          </div>
        </>
      )}
    </div>
  )
}

/** Tiroir latéral (plein écran sur mobile) : son fond ou Échap le ferment.
 *  Échap laisse la main au formulaire (il protège son brouillon) et à la
 *  confirmation en cours. */
export function Sheet({ open, onClose, label, children }: { open: boolean; onClose: () => void; label: string; children: JSX.Element }): JSX.Element | null {
  const panel = useRef<HTMLDivElement>(null)
  const { store, toasts } = useFeedbaacks()
  useEffect(() => {
    if (!open) return
    panel.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !store.get().composerOpen && !toasts.get().confirm) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, store, toasts])
  if (!open) return null
  return (
    <div class="fb-sheet-backdrop" data-feedbaacks-overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="fb-sheet" role="dialog" aria-modal="true" aria-label={label} tabIndex={-1} ref={panel} data-testid="feedbaacks-sheet">
        {children}
      </div>
    </div>
  )
}
